import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  CommonModule,
  DOCUMENT,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import { SwiperContainer } from 'swiper/element';
import { ApiService } from '../../../services/api.service';
import { finalize } from 'rxjs';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { UtilityService } from '../../../services/utility.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-hero-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-carousel.component.html',
  styleUrl: './hero-carousel.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeroCarouselComponent
  implements OnInit, OnDestroy, ApiErrorHandler
{
  isLoading: boolean = false;
  banners: any;
  bannerList: any[] = [];
  bannerMobile: any[] = [];
  bannerTablet: any[] = [];
  screenSize: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  swiperReady = false;
  predefinedBannerList: any = environment.predefinedBannerList;

  @ViewChild('swiperContainerHeroCarousel')
  private swiperContainer!: ElementRef<any>;
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(DOCUMENT) private document: Document,
    public toaster: ToasterService,
    private apiService: ApiService,
    private utilService: UtilityService,
  ) {}

  ngOnInit() {
    this.setPreloadImage()
    if (isPlatformBrowser(this.platformId)) {
      this.screenSize = this.utilService.getScreenSizeType();
      this.bannerList = [this.predefinedBannerList[`${this.screenSize}`]];
      // this.setInitialBanner(); //commenting for now
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize);
      this.fetchBanners(this.screenSize);
    }
  }



  setPreloadImage() {
    const banners = environment.predefinedBannerList;
    if (isPlatformServer(this.platformId)) {
    this.addPreloadLink(
      banners.desktop.imgUrl,
      '(min-width: 1024px)'
    );

    this.addPreloadLink(
      banners.tablet.imgUrl,
      '(min-width: 767px) and (max-width: 1023px)'
    );

    this.addPreloadLink(
      banners.mobile.imgUrl,
      '(max-width: 766px)'
    );
  }
  }

    public addPreloadLink(url: string, media?: string) {
  const existing = this.document.head.querySelector(
    `link[rel="preload"][href="${url}"]`
  );

  // avoid duplicates during navigation
  if (existing) return;

  const link = this.document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  link.setAttribute('fetchpriority', 'high');

  if (media) {
    link.media = media;
  }

  this.document.head.appendChild(link);
}

  setInitialBanner() {
    this.bannerList = [this.predefinedBannerList[`${this.screenSize}`]];
    if (this.bannerList.length && isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => {
        this.initSwiperCarousel();
      });
    } else {
      const swiperContainer: SwiperContainer = this.document.querySelector(
        '#hero-swiper-container',
      )!;
      swiperContainer?.swiper?.update();
      // this.swiperReady = true;
    }
  }

  private handleResize() {
    const newScreenSize = this.utilService.getScreenSizeType();
    if (newScreenSize !== this.screenSize) {
      this.screenSize = newScreenSize;    this.bannerList = [this.predefinedBannerList[this.screenSize]];
      window.location.reload();
    }
  }

  onBannerClick(currentIndex: number) {
    const banner = this.bannerList[currentIndex];
    if (!banner.isClickable) return;
    window.open(banner.link, banner.isExternal ? '_blank' : '_self');
  }

  private initSwiperCarousel() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1,
      speed: 500,
      spaceBetween: 0,
      navigation: {
        prevEl: '.hero-carousel-arrow-prev',
        nextEl: '.hero-carousel-arrow-next',
      },
      followFinger: true,
      pagination: {
        el: '.hero-swiper-pagination ',
        clickable: true,
      },
      loop: true,
      autoplay: {
        delay: 5000,
        stopOnLastSlide: false,
      },
      breakpoints: {
        768: {
          slidesPerView: 1,
        },
      },
    };
    Object.assign(this.swiperContainer.nativeElement, swiperProps);
    (<SwiperContainer>this.swiperContainer.nativeElement).initialize();
    this.swiperReady = true;
  }

  fetchBanners(screenSize: 'desktop' | 'tablet' | 'mobile') {
    this.apiService
      .fetchBanners(screenSize)
      .pipe(finalize(() => {}))
      .subscribe({
        next: (resp) => {
          if (!resp.list) {
            return;
          }
          const bannerList = resp?.list;
          this.banners = [...bannerList];
          this.banners = this.utilService.sortListData(this.banners, 'weight');
          // this.bannerList = [
          //   ...this.bannerList,
          //   ...this.mapBanners(screenSize),
          // ];
          const fixedBanner = this.predefinedBannerList[screenSize];
          const apiBanners = this.mapBanners(screenSize);

          // Optional: remove duplicates if API accidentally sends same banner
          const filteredApiBanners = apiBanners.filter(
            banner => banner.imgUrl !== fixedBanner?.imgUrl
          );

          this.bannerList = [
            fixedBanner,
            ...filteredApiBanners
          ];
          // uncommenting for now as sometime carousel is not loading;
          if (this.bannerList.length && isPlatformBrowser(this.platformId)) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            const swiperContainer: SwiperContainer =
              this.document.querySelector('#hero-swiper-container')!;
            swiperContainer?.swiper?.update();
          }
          // setTimeout(() => {
          //   this.isLoading = false;
          // }, 0);
        },
        error: apiErrorHandler.bind(this),
      });
  }

  private mapBanners(screenSize: 'desktop' | 'tablet' | 'mobile'): any[] {
    return this.banners.map((item: any, index: number) => ({
      imgUrl: item.files[screenSize].filePath,
      link: item.link,
      isClickable: item.isClickable,
      isExternal: item?.isExternal || false,
    }));
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.handleResize);
    }
  }
}
